import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import en from './locales/en.json';
import ja from './locales/ja.json';

export const LOCALES = ['en', 'ja'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export type MessageKey = keyof typeof en;

const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { en, ja };

const STORAGE_KEY = 'mcg_locale';

export type MessageValues = Record<string, string | number>;

export function toLocale(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const base = value.trim().toLowerCase().split(/[-_]/)[0];
  return (LOCALES as readonly string[]).includes(base ?? '')
    ? (base as Locale)
    : null;
}

function localeFromUrl(): Locale | null {
  if (typeof window === 'undefined') return null;
  return toLocale(new URLSearchParams(window.location.search).get('lang'));
}

function localeFromStorage(): Locale | null {
  try {
    return toLocale(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function localeFromBrowser(): Locale | null {
  const candidates = navigator.languages ?? [navigator.language];
  for (const tag of candidates) {
    const match = toLocale(tag);
    if (match) return match;
  }
  return null;
}

export function resolveInitialLocale(): Locale {
  return (
    localeFromUrl() ?? localeFromStorage() ?? localeFromBrowser() ?? DEFAULT_LOCALE
  );
}

export function format(template: string, values?: MessageValues): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>

    key in values ? String(values[key]) : whole,
  );
}

export interface I18nValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: MessageKey, values?: MessageValues) => string;
  htmlLang: string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? resolveInitialLocale(),
  );

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private mode or a full quota: the in-memory locale still applies.
    }

    document.documentElement.lang = next;
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === 'ja' ? 'ja-JP' : 'en';
  }, [locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (key, values) => format(DICTIONARIES[locale][key] ?? en[key], values),
      htmlLang: locale === 'ja' ? 'ja-JP' : 'en',
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error('useI18n must be used inside <I18nProvider>');
  }
  return value;
}

export function useT(): I18nValue['t'] {
  return useI18n().t;
}

export function useTranslate(): (key: string, fallback: string) => string {
  const { locale } = useI18n();
  return (key, fallback) => {
    const dictionary: Record<string, string> = DICTIONARIES[locale] ?? en;
    return dictionary[key] ?? (en as Record<string, string>)[key] ?? fallback;
  };
}

export function useOptionalT(): I18nValue['t'] {
  const value = useContext(I18nContext);
  if (value) return value.t;
  return (key, values) => format(en[key] ?? key, values);
}